import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { onAccountsSnapshot } from '../services/accountService';
import { onTransactionsSnapshot } from '../services/transactionService';
import { onGroupsSnapshot } from '../services/groupService';
import { useAuth } from './AuthContext';

const FilterContext = createContext();

export const FilterProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [allAccounts, setAllAccounts] = useState([]); // Todas las cuentas (incluyendo archivadas)
    const [transactions, setTransactions] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [mode, setMode] = useState('month'); // 'day' or 'month'
    const [showArchived, setShowArchived] = useState(false); // Mostrar/ocultar cuentas archivadas
    const [dateFilter, setDateFilter] = useState(mode === 'day' ? {
        startDate: new Date(),
        endDate: new Date(),
    } : {
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
    });

    // Cargar cuentas del usuario
    const loadAccounts = useCallback(() => {
        if (!currentUser) return () => { };
        const unsubscribeAccounts = onAccountsSnapshot(currentUser.uid, (accountsData) => {
            // Guardar TODAS las cuentas (incluyendo archivadas)
            setAllAccounts(accountsData);
        });

        return () => {
            unsubscribeAccounts();
        };
    }, [currentUser]);

    // Actualizar datos según la página actual
    const updateData = useCallback(() => {
        if (!currentUser) return () => { };

        // Usar TODAS las cuentas (incluyendo archivadas) para cargar transacciones
        const accountIds = allAccounts.map(account => account.id);

        let unsubscribeTransactions;
        unsubscribeTransactions = onTransactionsSnapshot(
            accountIds,
            dateFilter.startDate,
            dateFilter.endDate,
            (transactionsData) => {
                setTransactions(transactionsData);
                // console.log('Transactions updated:', transactionsData);
            }
        );

        const unsubscribeGroups = onGroupsSnapshot(currentUser.uid, (groupsData) => {
            setGroups(groupsData);
        });

        return () => {
            unsubscribeTransactions && unsubscribeTransactions();
            unsubscribeGroups && unsubscribeGroups();
        };
    }, [currentUser, allAccounts, dateFilter]);

    useEffect(() => {
        const storedMode = localStorage.getItem('mode');
        if (storedMode) {
            setMode(storedMode);
        }
    }, []);

    // Cargar cuentas al montar el componente
    useEffect(() => {
        const unsubscribeAccounts = loadAccounts();
        return () => unsubscribeAccounts && unsubscribeAccounts();
    }, [loadAccounts]);

    // Actualizar datos al cambiar la página o el filtro de fecha
    useEffect(() => {
        const unsubscribe = updateData();
        return () => unsubscribe && unsubscribe();
    }, [updateData]);

    useEffect(() => {
        localStorage.setItem('mode', mode);
    }, [mode]);

    // Filtrar cuentas archivadas según showArchived
    const accounts = useMemo(() => {
        return showArchived
            ? allAccounts
            : allAccounts.filter(account => !account.archived);
    }, [allAccounts, showArchived]);

    // Filtrar cuentas por grupo seleccionado
    const filteredAccounts = useMemo(() => {
        if (!selectedGroup) return accounts;
        const group = groups.find(group => group.groupId === selectedGroup);
        if (!group) return accounts;
        return accounts.filter(account => group.cuentas.includes(account.id));
    }, [accounts, groups, selectedGroup]);

    // Actualizar el filtro de fecha
    const updateDateFilter = useCallback((date) => {
        // console.log('Date filter updated:', date);
        setDateFilter(date);
    }, []);

    // Actualizar el grupo seleccionado
    const setSelectedGroupMemoized = useCallback((groupId) => {
        setSelectedGroup(groupId);
    }, []);

    // ...existing code...
    const value = useMemo(() => ({
        accounts: filteredAccounts,
        allAccounts, // Exponer todas las cuentas para Transactions
        transactions,
        groups,
        selectedGroup,
        setSelectedGroup: setSelectedGroupMemoized,
        updateData,
        dateFilter,
        updateDateFilter,
        mode,
        setMode,
        showArchived,
        setShowArchived,
        toggleShowArchived: () => setShowArchived(!showArchived)
    }), [
        filteredAccounts,
        allAccounts,
        transactions,
        groups,
        selectedGroup,
        setSelectedGroupMemoized,
        updateData,
        dateFilter,
        updateDateFilter,
        mode,
        setMode,
        showArchived
    ]);

    return (
        <FilterContext.Provider value={value}>
            {children}
        </FilterContext.Provider>
    );
};

export const useFilter = () => {
    return useContext(FilterContext);
};